import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, Divider, CircularProgress, Card, IconButton, Alert,
  useMediaQuery, useTheme, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Radio, RadioGroup, FormControlLabel, Tooltip, Paper, List, ListItem, ListItemText,
  Skeleton, LinearProgress,
} from '@mui/material';
import {
  LocalLibrary as LibraryIcon,
  Calculate as MathIcon, Science as ChemistryIcon, Bolt as PhysicsIcon,
  Public as GlobeIcon,
  Login as LoginIcon, ArrowForward as ArrowIcon,
  Add as AddIcon,
  Close as CloseIcon, Memory as CircuitIcon,
  Edit as EditIcon, Delete as DeleteIcon, Warning as WarningIcon,
  CheckBox as CheckBoxIcon, CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckCircle as CheckCircleIcon, Key as KeyIcon,
  PlayCircle as GetStartedNavIcon,
  School as HubFeaturesNavIcon,
  Tune as PersonalizeNavIcon,
  LocalOffer as PricingNavIcon,
  TrendingUp as JourneyNavIcon,
  Google as GoogleIcon,
  DriveFileMove as DriveIcon,
  VpnKey as ApiKeyIcon,
  Hub as HubIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ScienceOutlined as LabsNavIcon,
  AutoStories as HubLearnIcon,
  Psychology as HubExplainIcon,
  Sports as HubActivitiesIcon,
  FactCheck as HubQuizIcon,
  Notes as HubNotesIcon,
  Slideshow as HubSlidesIcon,
  Headphones as HubAudioIcon,
  Biotech as HubLabsIcon,
  Chat as HubChatIcon,
  CalendarMonth as PlanNavIcon,
  EventNote as StudyPlanTabIcon,
  CloudSync as CloudSyncIcon,
  Bolt as SmartLabIcon,
  AssignmentTurnedIn as StepDoneIcon,
  Replay as ResumeIcon2,
  Feedback as FeedbackIcon,
} from '@mui/icons-material';
import { hasAnyAIConfigured, hasDefaultKey, PROVIDERS } from '../services/llmService';
import { createRazorpayCheckout } from '../services/razorpayService';
import { FREE_ACCESS_MODE } from '../services/subscriptionService';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { getAllLearningHubs, createLearningHub, updateLearningHub, deleteLearningHub } from '../services/learningHubService';
import libraryService from '../services/libraryService';

// Heavy tool components — embedded directly in the right panel
import MathLabV2 from './tools/MathLabV2';
import ChemistryTools from './tools/ChemistryTools';
import PhysicsSimulator from './tools/PhysicsSimulator';
import CodeEditor from './tools/CodeEditor';
import GlobeViewer from './tools/GlobeViewer';
import CircuitSimulator from './CircuitSimulator';
import FeedbackDialog from './FeedbackDialog';


const FREE_FEATURES = [
  '3 AI queries per day',
  'Voice input & multilingual AI',
  'All AI tabs (Learn, Explain, etc.)',
  'Sample PDFs & basic tools',
];

const STUDENT_FEATURES = [
  'Unlimited AI queries with voice input',
  'All Labs: Math, Chemistry, Physics, Code, Globe',
  '50+ interactive tools & visualizations',
  'Google Drive sync & mobile optimized',
  'Priority AI response in any language',
];

// ─── Tab color map ─────────────────────────────────────────────────────────────
const TAB_COLORS = {
  getstarted:  '#1b496b',
  features:    '#f59e0b',
  labs:        '#10b981',
  personalize: '#a855f7',
  journey:     '#e11d48',
  pricing:     '#6366f1',
};

// ─── Bottom-nav tab definitions ──────────────────────────────────────────────
// Pricing tab is dropped while FREE_ACCESS_MODE is on (subscriptionService.js) —
// nothing to price when every signed-in user already has full access.
const INFO_TABS = [
  { id: 'getstarted',  label: 'Get Started',  icon: <GetStartedNavIcon /> },
  { id: 'features',    label: 'Hub Features', icon: <HubFeaturesNavIcon /> },
  { id: 'studyplan',   label: 'Study Plan',   icon: <PlanNavIcon /> },
  { id: 'labs',        label: 'Labs',         icon: <LabsNavIcon /> },
  { id: 'personalize', label: 'Personalize',  icon: <PersonalizeNavIcon /> },
  { id: 'journey',     label: 'Journey',      icon: <JourneyNavIcon /> },
  ...(FREE_ACCESS_MODE ? [] : [{ id: 'pricing', label: 'Pricing', icon: <PricingNavIcon /> }]),
];

// ─── Hub tab items for interactive grid ───────────────────────────────────────
const HUB_TAB_ITEMS = [
  {
    id: 'learn', icon: HubLearnIcon, color: '#1b496b',
    title: 'Learn',
    subtitle: 'AI-guided chapter study',
    desc: 'Transform any textbook chapter into structured AI explanations tailored to your level and language.',
    bullets: [
      'Chapter summaries with key concepts highlighted',
      'Exam-focused study tips and critical points',
      'Adjustable depth: Quick Summary → Expert Analysis (6 levels)',
      'Tracks what you\'ve read to build on prior context',
      'Auto-detects subject and adapts explanation style',
    ],
  },
  {
    id: 'explain', icon: HubExplainIcon, color: '#f59e0b',
    title: 'Explain',
    subtitle: 'Deep-dive any passage',
    desc: 'Select any text in your PDF and get an instant step-by-step AI breakdown — in your language.',
    bullets: [
      'Step-by-step concept unpacking with analogies',
      'Real-world connections to aid long-term retention',
      'Auto-language detection for 12 Indian languages',
      'Multi-page passage selection with unified explanation',
      'Clipboard-ready — copy explanation directly to Notes',
    ],
  },
  {
    id: 'activities', icon: HubActivitiesIcon, color: '#10b981',
    title: 'Activities',
    subtitle: 'Auto-generated exercises',
    desc: 'Interactive exercises and word games generated directly from your PDF content — not generic templates.',
    bullets: [
      'Fill-in-the-blank, matching, and ordering tasks',
      'Vocabulary reinforcement through game mechanics',
      'Word scrambles and glossary drills from PDF content',
      'Progress-aware — adapts to what you\'ve already studied',
      'Covers key terms, dates, formulas, and concepts',
    ],
  },
  {
    id: 'quiz', icon: HubQuizIcon, color: '#ef4444',
    title: 'Practice Quiz',
    subtitle: '9 types · Bloom\'s Taxonomy',
    desc: 'Research-backed quizzes aligned to Bloom\'s Taxonomy and Depth of Knowledge (DOK 1–4).',
    bullets: [
      'MCQ, short answer, true/false, matching + 5 more types',
      'Bloom\'s levels: Remember → Understand → Apply → Analyse → Create',
      'AI scores short-answer questions with 1-2 sentence explanations',
      'Per-question Bloom\'s + DOK level labels shown after submission',
      'Quiz results tracked in Learning Journey with trend analysis',
    ],
  },
  {
    id: 'notes', icon: HubNotesIcon, color: '#6366f1',
    title: 'Notes',
    subtitle: 'Rich text editor, per PDF',
    desc: 'A powerful auto-saving notes editor attached to each PDF in your hub — always organised.',
    bullets: [
      'Markdown support with live preview',
      'Per-PDF notes — never lose context across uploads',
      'Copy-to-Notes button on every AI response in the hub',
      'Export notes as a formatted PDF',
      'Auto-saves every keystroke — no manual save needed',
    ],
  },
  {
    id: 'slides', icon: HubSlidesIcon, color: '#a855f7',
    title: 'HTML Slides',
    subtitle: '4 styles · export PDF/HTML',
    desc: 'Generate polished, presentation-ready slides from any chapter — in seconds.',
    bullets: [
      '4 visual styles: Academic, Modern, Minimal, Creative',
      '3 detail levels: Key Points, Standard, Comprehensive',
      'Export to PDF or standalone HTML file',
      'Includes diagrams, bullet points, and speaker notes',
      'One-click sync to Google Drive',
    ],
  },
  {
    id: 'audio', icon: HubAudioIcon, color: '#06b6d4',
    title: 'Audio Explainer',
    subtitle: 'Learn by listening',
    desc: 'Turn your study material into an audio lecture or Q&A session — ideal for revision on the go.',
    bullets: [
      'Lecture or Q&A format, 3 / 5 / 10 min durations',
      'Playback speed control and paragraph-sync text highlight',
      'Great for commutes, revision, and auditory learners',
      'Text highlight follows audio playback in real-time',
      'Saved to Google Drive as a portable HTML audio player',
    ],
  },
  {
    id: 'interactivelabs', icon: HubLabsIcon, color: '#14b8a6',
    title: 'Interactive Labs',
    subtitle: 'Smart simulator routing',
    desc: 'Gamified, hands-on activities auto-generated from your PDF — and routed to the right built-in simulator.',
    bullets: [
      'Auto-detects topic and launches the matching simulator',
      'Circuits → Circuit Simulator · Molecules → Chemistry Lab',
      'Physics experiments, Code Editor, Math Lab, Globe Explorer',
      'Quiz Game, Sorting, Matching, Timeline, and Simulation labs',
      'Lab scores tracked in your Learning Journey Bloom\'s radar',
    ],
  },
  {
    id: 'chat', icon: HubChatIcon, color: '#ec4899',
    title: 'Vyonn Hub Chat',
    subtitle: 'Your AI study partner',
    desc: 'Ask anything about your PDF. Vyonn explains, solves equations, and responds in your language.',
    bullets: [
      'Modes: Learn · Explain · Solve · Equation (LaTeX rendering)',
      'Voice input and voice output in 12 Indian languages',
      'Context-aware — always grounded in your uploaded PDF',
      'Retains full conversation history per PDF',
      'Chat history exported as formatted HTML to Google Drive',
    ],
  },
  {
    id: 'studyplan', icon: StudyPlanTabIcon, color: '#e11d48',
    title: 'Study Plan',
    subtitle: 'AI day-by-day schedule',
    desc: 'Get a personalised study plan for any PDF or multi-chapter hub — built around your target date and learning style.',
    bullets: [
      'AI questionnaire: mastery level, study hours, learning approach',
      'Day-by-day schedule: Slides → Quiz → Labs → Practice Exam per step',
      'Auto-detects chapter number, subject, and Bloom\'s level',
      'All progress, scores, and generated content synced to Google Drive',
      'Resume exactly where you left off — even after closing the app',
    ],
  },
];

// ─── Info section components ──────────────────────────────────────────────────

function GetStartedSection({ onSignIn, user, driveConnected, onOpenSettings, hubCount = 0, onSelectNav }) {
  const isSignedIn = !!user;
  const isDriveReady = !!driveConnected;
  const [hasAnyAIKey, setHasAnyAIKey] = useState(false);

  useEffect(() => {
    hasAnyAIConfigured().then(setHasAnyAIKey).catch(() => setHasAnyAIKey(false));
  }, [user]);

  // Determine completion status for each step
  const stepDone = [isSignedIn, isDriveReady, hasAnyAIKey, isSignedIn && hubCount > 0];
  // Auto-expand the first incomplete step
  const firstIncomplete = stepDone.indexOf(false);
  const [expanded, setExpanded] = useState(firstIncomplete >= 0 ? firstIncomplete : -1);

  const steps = [
    {
      icon: <GoogleIcon sx={{ fontSize: 17 }} />, color: '#ea4335',
      title: 'Sign in with Google',
      subtitle: isSignedIn
        ? `Signed in as ${user.displayName || user.email}`
        : 'No password needed — secure OAuth, one click',
      done: isSignedIn,
      points: isSignedIn
        ? ['You are signed in — your hubs, notes, and progress are synced to your account']
        : [
          'Click Sign in with Google on the home screen',
          'We never see your password — secure OAuth only',
          'Your hubs, notes, and progress are tied to your account',
        ],
      action: !isSignedIn && onSignIn ? (
        <Button
          variant="outlined" size="small" startIcon={<GoogleIcon sx={{ fontSize: 14 }} />}
          onClick={onSignIn}
          sx={{ mt: 1.25, borderRadius: 1.5, borderColor: '#ea4335', color: '#ea4335', textTransform: 'none', fontWeight: 600, fontSize: '0.73rem', py: 0.5, '&:hover': { bgcolor: '#ea433510' } }}
        >
          Sign in now
        </Button>
      ) : null,
    },
    {
      icon: <DriveIcon sx={{ fontSize: 17 }} />, color: '#10b981',
      title: 'Connect Google Drive',
      subtitle: isDriveReady
        ? 'Google Drive is connected — your files sync automatically'
        : isSignedIn
          ? 'Accept the Drive permission to enable file sync'
          : 'Your PDFs live in your Drive — not our servers',
      done: isDriveReady,
      points: isDriveReady
        ? [
          'Your Google Drive is connected and syncing',
          'PDFs and generated resources are saved to your "Ekamanam" folder',
          'Switch devices anytime — hubs sync automatically',
        ]
        : [
          'Accept the Drive permission prompt after signing in',
          'Ekamanam creates an "Ekamanam" folder in your own Drive',
          'Switch devices anytime — hubs sync automatically',
        ],
    },
    {
      icon: <ApiKeyIcon sx={{ fontSize: 17 }} />, color: '#f59e0b',
      title: 'Activate Ekamanam Learning Engine',
      subtitle: hasAnyAIKey
        ? 'Ekamanam is fully activated — all learning tools are ready'
        : 'One API key powers all of Ekamanam — takes under 2 minutes',
      highlight: !hasAnyAIKey,
      done: hasAnyAIKey,
      pathSteps: hasAnyAIKey ? null : [
        { icon: <SettingsIcon sx={{ fontSize: 13 }} />, label: 'Settings' },
        { label: 'Providers' },
        { label: 'Paste Key' },
        { label: 'Save' },
      ],
      points: hasAnyAIKey
        ? [
          'Ekamanam is active — Learn, Quiz, Audio, Labs, and all tools are powered up',
          'Your data stays private — queries go directly from your browser to the provider',
          'Add more providers in Settings for additional speed and fallback options',
        ]
        : [
          'Ekamanam sends queries directly from your browser to the provider — your data stays private',
          'Google Gemini\'s free tier covers most students — you only pay for what you use',
          'Your own key means no rate limits, no throttling — Ekamanam runs at full speed',
        ],
      tip: hasAnyAIKey ? null : 'Use the Quick AI Setup wizard in Settings — it walks you through Gemini, Groq, and Claude with direct signup links.',
      chips: hasAnyAIKey ? null : ['Gemini 2.5 Flash (free)', 'Groq Qwen3.6 (fast)', 'Claude Sonnet 5 (quality)'],
      action: !hasAnyAIKey && isSignedIn && onOpenSettings ? (
        <Button
          variant="outlined" size="small" startIcon={<SettingsIcon sx={{ fontSize: 14 }} />}
          onClick={onOpenSettings}
          sx={{ mt: 1.25, borderRadius: 1.5, borderColor: '#f59e0b', color: '#f59e0b', textTransform: 'none', fontWeight: 600, fontSize: '0.73rem', py: 0.5, '&:hover': { bgcolor: '#f59e0b10' } }}
        >
          Open Settings
        </Button>
      ) : null,
    },
    {
      icon: <HubIcon sx={{ fontSize: 17 }} />, color: '#1b496b',
      title: hubCount > 0 ? `You have ${hubCount} Learning Hub${hubCount !== 1 ? 's' : ''}` : 'Create Your First Learning Hub',
      subtitle: hubCount > 0
        ? 'Your hubs are ready — dive in and start learning!'
        : 'Your workspace for one subject — all tools in one place',
      done: isSignedIn && hubCount > 0,
      points: hubCount > 0
        ? [
            `You\'ve created ${hubCount} hub${hubCount !== 1 ? 's' : ''} — great start!`,
            'Click on Learning Hubs in the sidebar to open your hubs',
            'Each hub gives you AI-powered Learn, Quiz, Slides, Audio, Labs & more',
          ]
        : [
            'Create a Hub — give it a name and pick an icon',
            'Upload your textbook PDF (or try a sample hub)',
            'Unlock 9+ study tools: Learn, Quiz, Slides, Audio, Chat, Labs…',
          ],
      action: isSignedIn ? (
        hubCount > 0 ? (
          <Button
            variant="outlined" size="small" startIcon={<LibraryIcon sx={{ fontSize: 14 }} />}
            onClick={() => onSelectNav?.('hubs')}
            sx={{ mt: 1.25, borderRadius: 1.5, borderColor: '#1b496b', color: '#1b496b', textTransform: 'none', fontWeight: 600, fontSize: '0.73rem', py: 0.5, '&:hover': { bgcolor: '#1b496b10' } }}
          >
            Open Learning Hubs
          </Button>
        ) : (
          <Button
            variant="outlined" size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={() => onSelectNav?.('hubs')}
            sx={{ mt: 1.25, borderRadius: 1.5, borderColor: '#1b496b', color: '#1b496b', textTransform: 'none', fontWeight: 600, fontSize: '0.73rem', py: 0.5, '&:hover': { bgcolor: '#1b496b10' } }}
          >
            Create Your First Hub
          </Button>
        )
      ) : null,
    },
  ];

  return (
    <Box sx={{ p: { xs: 2.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 2, fontSize: '0.62rem' }}>
        {isSignedIn ? 'Your Setup Progress' : 'How it works'}
      </Typography>
      <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2, letterSpacing: '-0.3px', mb: 0.25 }}>
        {isSignedIn ? `${stepDone.filter(Boolean).length} of 4 Steps Complete` : 'Get Started in 4 Steps'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.6, fontSize: '0.8rem' }}>
        Ekamanam connects to your own provider keys — your data stays yours and learning costs go directly to the provider.
      </Typography>

      {steps.map((step, idx) => {
        const isOpen = expanded === idx;
        const isDone = step.done;
        return (
          <Box
            key={idx}
            sx={{
              borderRadius: 2, border: '1px solid',
              borderColor: isDone ? '#10b98140' : isOpen ? `${step.color}50` : 'divider',
              bgcolor: isDone ? '#10b98108' : isOpen && step.highlight ? `${step.color}06` : isOpen ? `${step.color}04` : 'background.default',
              overflow: 'hidden', transition: 'border-color 0.2s, background 0.2s',
            }}
          >
            {/* Step header — always visible, click to toggle */}
            <Box
              onClick={() => setExpanded(isOpen ? -1 : idx)}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, cursor: 'pointer', userSelect: 'none' }}
            >
              {/* Step number circle — green check when done */}
              <Box sx={{
                width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                bgcolor: isDone ? '#10b981' : isOpen ? step.color : `${step.color}20`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.2s ease',
                boxShadow: isDone ? '0 3px 10px #10b98145' : isOpen ? `0 3px 10px ${step.color}45` : 'none',
              }}>
                {isDone
                  ? <CheckCircleIcon sx={{ fontSize: 20, color: 'white' }} />
                  : <Typography sx={{ fontSize: '0.9rem', fontWeight: 900, color: isOpen ? 'white' : step.color, lineHeight: 1 }}>
                      {idx + 1}
                    </Typography>
                }
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.85rem', lineHeight: 1.3, color: isDone ? '#10b981' : 'text.primary' }}>
                  {step.title}
                </Typography>
                <Typography variant="caption" color={isDone ? '#10b981' : 'text.secondary'} sx={{ lineHeight: 1.4 }}>{step.subtitle}</Typography>
              </Box>
              <ExpandMoreIcon sx={{
                fontSize: 17, color: 'text.disabled', flexShrink: 0,
                transform: isOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s ease',
              }} />
            </Box>

            {/* Expandable body */}
            <Collapse in={isOpen}>
              <Box sx={{ px: 2, pb: 1.75 }}>
                {/* Visual navigation path — shown for API Keys step */}
                {step.pathSteps && (
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.4, mb: 1.25, p: 1, bgcolor: `${step.color}08`, borderRadius: 1.5, border: `1px solid ${step.color}25` }}>
                    <Typography variant="caption" sx={{ color: step.color, fontWeight: 700, mr: 0.5 }}>Where to find it:</Typography>
                    {step.pathSteps.map((ps, i) => (
                      <React.Fragment key={i}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, px: 0.75, py: 0.2, borderRadius: 1, bgcolor: 'background.paper', border: `1px solid ${step.color}30` }}>
                          {ps.icon}
                          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: step.color, lineHeight: 1 }}>{ps.label}</Typography>
                        </Box>
                        {i < step.pathSteps.length - 1 && (
                          <Typography sx={{ fontSize: '0.65rem', color: step.color, fontWeight: 700 }}>›</Typography>
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: step.chips || step.tip || step.action ? 1 : 0 }}>
                  {step.points.map((pt, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                      <CheckCircleIcon sx={{ fontSize: 12, color: step.color, mt: '3px', flexShrink: 0 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.65 }}>{pt}</Typography>
                    </Box>
                  ))}
                </Box>
                {step.chips && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                    {step.chips.map((c, i) => (
                      <Chip key={i} label={c} size="small" sx={{ fontSize: '0.62rem', height: 18, bgcolor: `${step.color}12`, color: step.color, border: 'none' }} />
                    ))}
                  </Box>
                )}
                {step.tip && (
                  <Box sx={{ mt: 0.75, p: 1, bgcolor: '#1b496b07', borderRadius: 1.5, border: '1px solid #1b496b1a' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                      💡 {step.tip}
                    </Typography>
                  </Box>
                )}
                {step.action}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Study Plan Info Section ──────────────────────────────────────────────────
function StudyPlanInfoSection() {
  const STEPS = [
    { n: '1', title: 'Answer 3 quick questions', body: 'Tell Ekamanam your mastery level, daily study hours, target exam date, and preferred learning style.' },
    { n: '2', title: 'AI builds your day-by-day plan', body: 'A personalized schedule is generated: Slides → Quiz → Interactive Lab → Practice Exam, for every chapter.' },
    { n: '3', title: 'Execute, track & resume', body: 'Complete steps at your own pace. Progress and generated content are saved automatically to Google Drive.' },
  ];

  const FEATURES = [
    { icon: '📅', label: 'Personalized Schedule', body: 'Built around your target date and available study hours.' },
    { icon: '☁️', label: 'Drive Sync', body: 'Every step, score, and generated file synced to Google Drive.' },
    { icon: '⚡', label: 'Smart Lab Routing', body: 'Labs launch the real Circuit, Physics, or Chemistry simulator by topic.' },
    { icon: '🎯', label: 'Post-step Quizzes', body: 'A practice exam follows every Slides or Audio step automatically.' },
    { icon: '📖', label: 'Chapter Detection', body: 'Auto-detects chapter number, subject, and Bloom\'s level from the PDF.' },
    { icon: '🔄', label: 'Resume Anywhere', body: 'Close the app and return days later — your plan remembers everything.' },
    { icon: '🧠', label: 'Bloom\'s Tracking', body: 'Every completed step is mapped to a Bloom\'s Taxonomy level.' },
    { icon: '📚', label: 'Multi-PDF Plans', body: 'One plan can span an entire textbook or all chapters in a hub.' },
  ];

  return (
    <Box sx={{ p: { xs: 2.5, md: 3 }, overflowY: 'auto', height: '100%' }}>
      {/* Hero */}
      <Typography variant="overline" sx={{ color: '#e11d48', fontWeight: 700, letterSpacing: 2, fontSize: '0.62rem', display: 'block' }}>
        Primary Feature
      </Typography>
      <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2, letterSpacing: '-0.3px', mb: 0.75 }}>
        AI-Personalized Study Plans
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.75 }}>
        The only tool that coordinates all 10 study tabs into a single, day-by-day learning journey — personalised to your target date, mastery level, and preferred learning style.
      </Typography>

      {/* How it works */}
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        How it works
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2.5 }}>
        {STEPS.map(s => (
          <Box key={s.n} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Box sx={{
              width: 26, height: 26, borderRadius: '50%', bgcolor: '#e11d48',
              color: '#fff', fontWeight: 800, fontSize: '0.75rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>{s.n}</Box>
            <Box>
              <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.3 }}>{s.title}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55 }}>{s.body}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Feature cards 2-column grid */}
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        What's included
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 0.75, mb: 2.5 }}>
        {FEATURES.map(f => (
          <Box key={f.label} sx={{
            p: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
            bgcolor: 'background.default',
          }}>
            <Typography sx={{ fontSize: '1.1rem', mb: 0.4 }}>{f.icon}</Typography>
            <Typography variant="caption" fontWeight={700} sx={{ display: 'block', lineHeight: 1.3 }}>{f.label}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, fontSize: '0.7rem' }}>{f.body}</Typography>
          </Box>
        ))}
      </Box>

      {/* Drive sync callout */}
      <Box sx={{
        p: 1.5, borderRadius: 2, border: '1px solid', borderColor: '#e11d4830',
        bgcolor: '#e11d4808', display: 'flex', gap: 1.25, alignItems: 'flex-start',
      }}>
        <CloudSyncIcon sx={{ color: '#e11d48', fontSize: 20, flexShrink: 0, mt: 0.25 }} />
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          <strong>Every step synced to Google Drive.</strong> Slides, audio files, quiz results, and your progress are saved automatically — pick up where you left off on any device.
        </Typography>
      </Box>
    </Box>
  );
}

function HubFeaturesGrid() {
  const [selected, setSelected] = useState(null);
  const item = HUB_TAB_ITEMS.find(h => h.id === selected);

  return (
    <Box sx={{ p: { xs: 2.5, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="overline" sx={{ color: '#f59e0b', fontWeight: 700, letterSpacing: 2, fontSize: '0.62rem', display: 'block' }}>
        Study Tools
      </Typography>
      <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2, letterSpacing: '-0.3px', mb: 1.5, flexShrink: 0 }}>
        10 Tabs in Every Hub
      </Typography>

      <Box sx={{ flex: 1, display: 'flex', gap: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* Main area — crossfades between grid and detail */}
        <Box sx={{ flex: 1, position: 'relative', minWidth: 0 }}>

          {/* Grid view */}
          <Box sx={{
            position: 'absolute', inset: 0,
            opacity: selected ? 0 : 1,
            transform: selected ? 'translateX(-10px)' : 'translateX(0)',
            transition: 'opacity 0.22s ease, transform 0.22s ease',
            pointerEvents: selected ? 'none' : 'auto',
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75,
            overflowY: 'auto', alignContent: 'start',
          }}>
            {HUB_TAB_ITEMS.map(h => (
              <Box
                key={h.id}
                onClick={() => setSelected(h.id)}
                sx={{
                  p: 1.25, borderRadius: 1.75, border: '1px solid', borderColor: 'divider',
                  bgcolor: 'background.default', cursor: 'pointer', textAlign: 'center',
                  transition: 'all 0.15s ease',
                  '&:hover': { borderColor: h.color, bgcolor: `${h.color}08`, transform: 'translateY(-1px)', boxShadow: `0 3px 10px ${h.color}18` },
                }}
              >
                <h.icon sx={{ fontSize: '1.4rem', color: h.color, mb: 0.4 }} />
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, lineHeight: 1.2, color: 'text.primary' }}>{h.title}</Typography>
              </Box>
            ))}
          </Box>

          {/* Detail view */}
          <Box sx={{
            position: 'absolute', inset: 0,
            opacity: selected ? 1 : 0,
            transform: selected ? 'translateX(0)' : 'translateX(10px)',
            transition: 'opacity 0.22s ease, transform 0.22s ease',
            pointerEvents: selected ? 'auto' : 'none',
            overflowY: 'auto',
          }}>
            {item && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{
                    width: 42, height: 42, borderRadius: 2,
                    bgcolor: `${item.color}15`, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', flexShrink: 0,
                  }}>
                    <item.icon sx={{ fontSize: '1.5rem', color: item.color }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={800} sx={{ lineHeight: 1.2 }}>{item.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{item.subtitle}</Typography>
                  </Box>
                  <IconButton size="small" onClick={() => setSelected(null)} sx={{ flexShrink: 0 }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.75 }}>
                  {item.desc}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.85 }}>
                  {item.bullets.map((b, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: item.color, mt: '6px', flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ lineHeight: 1.65, fontSize: '0.82rem' }}>{b}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Animated icon strip — visible only when a card is selected */}
        <Box sx={{
          width: selected ? 44 : 0,
          overflow: 'hidden',
          transition: 'width 0.22s ease',
          display: 'flex', flexDirection: 'column', gap: 0.5,
          alignItems: 'center', pt: 0.25, flexShrink: 0, overflowY: 'auto',
        }}>
          {HUB_TAB_ITEMS.map(h => (
            <Box
              key={h.id}
              onClick={() => setSelected(h.id)}
              title={h.title}
              sx={{
                width: 34, height: 34, borderRadius: 1.5, cursor: 'pointer', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: selected === h.id ? `${h.color}20` : 'background.default',
                border: '1px solid', borderColor: selected === h.id ? h.color : 'divider',
                transition: 'all 0.15s ease',
                '&:hover': { bgcolor: `${h.color}14`, borderColor: h.color },
              }}
            >
              <h.icon sx={{ fontSize: '1.05rem', color: selected === h.id ? h.color : 'text.secondary' }} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function LabsSection() {
  const labs = [
    {
      emoji: '📐', title: 'Math Lab', color: '#3b82f6',
      tagline: 'Visualise every concept',
      highlights: ['Graph plotter & function explorer', 'Quadratic, Trig, Matrix, Probability', 'Step-by-step AI walkthroughs'],
    },
    {
      emoji: '🧪', title: 'Chemistry Lab', color: '#10b981',
      tagline: 'See molecules come alive',
      highlights: ['3D molecular viewer with labels', 'Periodic table & equation balancing', 'Virtual experiments with AI guide'],
    },
    {
      emoji: '⚡', title: 'Physics Simulator', color: '#a855f7',
      tagline: '16+ live simulations',
      highlights: ['Projectile, waves, collisions, pendulum', 'Adjust parameters in real time', 'AI explains what you\'re observing'],
    },
    {
      emoji: '🌍', title: 'Globe Explorer', color: '#06b6d4',
      tagline: 'Geography that moves',
      highlights: ['Interactive 3D globe', 'Countries, cities, trade routes, Wonders', 'AI geography guide built in'],
    },
    {
      emoji: '🔌', title: 'Circuit Lab', color: '#f59e0b',
      tagline: 'Build & simulate circuits',
      highlights: ['Drag-and-drop circuit builder', 'Component library & schematic editor', 'Logic gate & voltage visualizer'],
    },
  ];

  const [selected, setSelected] = useState(null);

  return (
    <Box sx={{ p: { xs: 2.5, md: 3 } }}>
      <Typography variant="overline" sx={{ color: '#10b981', fontWeight: 700, letterSpacing: 2, fontSize: '0.62rem', display: 'block' }}>
        Interactive Labs
      </Typography>
      <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2, letterSpacing: '-0.3px', mb: 0.5 }}>
        5 Educational Labs
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, fontSize: '0.8rem' }}>
        Go beyond the textbook. Each lab is a full interactive environment with real-time AI guidance built in.
      </Typography>

      {/* Lab cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {labs.map((l, i) => {
          const isOpen = selected === l.title;
          return (
            <Box
              key={i}
              onClick={() => setSelected(isOpen ? null : l.title)}
              sx={{
                borderRadius: 2, border: '1px solid',
                borderColor: isOpen ? l.color : 'divider',
                borderLeft: `4px solid ${l.color}`,
                bgcolor: isOpen ? `${l.color}07` : 'background.default',
                cursor: 'pointer', overflow: 'hidden',
                transition: 'all 0.18s ease',
                '&:hover': { borderColor: l.color, bgcolor: `${l.color}05` },
              }}
            >
              {/* Header row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.25 }}>
                <Box sx={{
                  width: 38, height: 38, borderRadius: 1.75, flexShrink: 0,
                  bgcolor: `${l.color}18`, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: '1.3rem',
                }}>
                  {l.emoji}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={700} sx={{ fontSize: '0.88rem', color: l.color, lineHeight: 1.2 }}>{l.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{l.tagline}</Typography>
                </Box>
                <ExpandMoreIcon sx={{
                  fontSize: 16, color: 'text.disabled', flexShrink: 0,
                  transform: isOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.18s ease',
                }} />
              </Box>
              {/* Expandable highlights */}
              <Collapse in={isOpen}>
                <Box sx={{ px: 2, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {l.highlights.map((h, j) => (
                    <Box key={j} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: l.color, mt: '6px', flexShrink: 0 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.65 }}>{h}</Typography>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 2, bgcolor: '#10b98108', border: '1px solid #10b98125' }}>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          🎤 Voice input in all labs &nbsp;·&nbsp; 🌐 12 language support &nbsp;·&nbsp; 📱 Mobile-optimised &nbsp;·&nbsp; 🧊 3D visuals (Three.js, 3Dmol.js, Plotly)
        </Typography>
      </Box>
    </Box>
  );
}

function PersonalizeSection() {
  const personas = [
    { emoji: '😊', name: 'Friendly Mentor', desc: 'Warm, encouraging, builds confidence' },
    { emoji: '📏', name: 'Strict Teacher', desc: 'Rigorous, no shortcuts, high standards' },
    { emoji: '🤔', name: 'Socratic Guide', desc: 'Asks questions, nurtures critical thinking' },
    { emoji: '🤝', name: 'Study Buddy', desc: 'Peer-level, collaborative, casual' },
    { emoji: '🎓', name: 'Expert Professor', desc: 'Deep analysis, academic depth' },
  ];
  const styles = [
    { emoji: '👁️', name: 'Visual', desc: 'Diagrams, charts, colour-coded' },
    { emoji: '👂', name: 'Auditory', desc: 'Voice-first, audio explanations' },
    { emoji: '📖', name: 'Reading/Writing', desc: 'Structured text, detailed notes' },
    { emoji: '🖐️', name: 'Kinesthetic', desc: 'Hands-on activities, simulations' },
  ];
  const languages = ['English','हिंदी','తెలుగు','தமிழ்','ಕನ್ನಡ','മലയാളം','বাংলা','मराठी','ગુજરાતી','ਪੰਜਾਬੀ','اردو','Sanskrit'];
  return (
    <Box sx={{ p: { xs: 2.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <Typography variant="overline" sx={{ color: '#a855f7', fontWeight: 700, letterSpacing: 2, fontSize: '0.62rem', display: 'block' }}>
          Personalise
        </Typography>
        <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2, letterSpacing: '-0.3px', mb: 0.5 }}>
          Learn Your Way
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25, fontSize: '0.8rem', lineHeight: 1.6 }}>
          Choose how your AI tutor communicates — set globally or per hub.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.75 }}>
          {personas.map((p, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
              <Box sx={{ fontSize: '1.1rem', lineHeight: 1, flexShrink: 0 }}>{p.emoji}</Box>
              <Box>
                <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2, fontSize: '0.78rem' }}>{p.name}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>{p.desc}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      <Box>
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'text.primary', mb: 1 }}>
          4 Learning Styles
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
          {styles.map((s, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
              <Box sx={{ fontSize: '1.1rem', lineHeight: 1, flexShrink: 0 }}>{s.emoji}</Box>
              <Box>
                <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2, fontSize: '0.78rem' }}>{s.name}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>{s.desc}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      <Box>
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'text.primary', mb: 1 }}>
          12 Chat Languages
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
          {languages.map((lang, i) => (
            <Chip key={i} label={lang} size="small" sx={{ fontSize: '0.7rem', height: 22, bgcolor: '#1b496b10', color: '#1b496b', border: 'none' }} />
          ))}
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block', lineHeight: 1.6 }}>
          Auto-detection in Vyonn chat — or set a default language per hub in Hub Settings.
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Learning Journey Section ─────────────────────────────────────────────────

const JOURNEY_FEATURES = [
  {
    emoji: '🔥',
    color: '#f59e0b',
    title: 'Study Streak & Heatmap',
    tagline: 'See every study session at a glance',
    highlights: [
      'GitHub-style contribution heatmap across the full calendar year',
      'Live streak counter + longest streak badge to build daily habits',
      'Recent activity feed grouped by day, hub, and PDF',
    ],
  },
  {
    emoji: '🧠',
    color: '#a855f7',
    title: "Bloom's Taxonomy Analysis",
    tagline: 'AI maps your cognitive depth — across all 6 levels',
    highlights: [
      'Every quiz, activity, and explanation is AI-classified to Bloom\'s framework',
      'Radar chart: Remember → Understand → Apply → Analyse → Evaluate → Create',
      'Pinpoints gaps — e.g. "You recall well but rarely practise Evaluation"',
    ],
  },
  {
    emoji: '✨',
    color: '#e11d48',
    title: 'AI-Generated Insights',
    tagline: 'A personal coach based on your actual study data',
    highlights: [
      'Surfaces real strengths: topics, formats, and times you excel at',
      'Flags patterns: topic avoidance, low variety, late-night fatigue',
      'Generates specific, actionable suggestions for your next session',
    ],
  },
  {
    emoji: '📊',
    color: '#10b981',
    title: 'Learning Analytics',
    tagline: 'Know how you actually study — not just how much',
    highlights: [
      'Mode breakdown: time split across Learn, Quiz, Explain, Slides, Chat',
      'Hour-of-day heat strip — when are you most cognitively active?',
      'Top hubs by time + AI interaction count visualised over weeks',
    ],
  },
  {
    emoji: '🏆',
    color: '#6366f1',
    title: 'Depth of Knowledge (DOK)',
    tagline: 'Research-backed quiz difficulty levels 1 – 4',
    highlights: [
      'Practice Quiz generates questions at DOK levels 1 (recall) through 4 (extended thinking)',
      'DOK 3 & 4 questions require analysis, reasoning, and real-world application',
      'AI evaluation explains exactly why each answer is right or wrong',
    ],
  },
];

function LearningJourneySection() {
  const [expanded, setExpanded] = useState(null);

  const toggle = (i) => setExpanded(prev => prev === i ? null : i);

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
      {/* Header */}
      <Typography variant="overline" sx={{
        fontSize: '0.6rem', fontWeight: 800, letterSpacing: 2.5,
        color: '#e11d48', display: 'block', mb: 0.4,
      }}>
        Learning Intelligence
      </Typography>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 0.75, letterSpacing: '-0.3px' }}>
        Your Learning Journey
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
        Ekamanam doesn't just help you study — it tracks how you learn. Every session builds a rich picture of your cognitive growth, study habits, and mastery depth.
      </Typography>

      {/* Feature accordion rows */}
      {JOURNEY_FEATURES.map((f, i) => {
        const open = expanded === i;
        return (
          <Box
            key={i}
            onClick={() => toggle(i)}
            sx={{
              mb: 1, borderRadius: 2, border: '1px solid',
              borderColor: open ? f.color + '50' : 'divider',
              overflow: 'hidden', cursor: 'pointer',
              bgcolor: open ? f.color + '06' : 'transparent',
              transition: 'border-color 0.18s, background 0.18s',
              '&:hover': { borderColor: f.color + '60', bgcolor: f.color + '06' },
            }}
          >
            {/* Row header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25 }}>
              <Typography sx={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>{f.emoji}</Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700} sx={{ color: open ? f.color : 'text.primary' }}>
                  {f.title}
                </Typography>
                {!open && (
                  <Typography variant="caption" color="text.secondary" noWrap>{f.tagline}</Typography>
                )}
              </Box>
              <ExpandMoreIcon sx={{
                fontSize: 18, color: 'text.disabled', flexShrink: 0,
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }} />
            </Box>

            {/* Expanded body */}
            <Collapse in={open}>
              <Box sx={{ px: 2.5, pb: 2, pt: 0.25 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
                  {f.tagline}
                </Typography>
                {f.highlights.map((h, j) => (
                  <Box key={j} sx={{ display: 'flex', gap: 1, mb: 0.6 }}>
                    <Box sx={{
                      width: 6, height: 6, borderRadius: '50%', mt: '5px', flexShrink: 0,
                      bgcolor: f.color,
                    }} />
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55 }}>{h}</Typography>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
        );
      })}

      {/* Footer note */}
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5, lineHeight: 1.6 }}>
        Learning Journey data is stored locally and in your Google account — never shared with third parties.
      </Typography>
    </Box>
  );
}

// Top tab navigation bar — used at the top of the right panel and mobile sidebar
function InfoTopNav({ infoTab, setInfoTab, compact = false }) {
  return (
    <Box sx={{
      borderBottom: '1px solid', borderColor: 'divider',
      display: 'flex', justifyContent: 'space-around', alignItems: 'stretch',
      px: 0.5, flexShrink: 0,
    }}>
      {INFO_TABS.map(tab => {
        const color = TAB_COLORS[tab.id] || '#1b496b';
        const active = infoTab === tab.id;
        return (
          <Box
            key={tab.id}
            onClick={() => setInfoTab(tab.id)}
            sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 0.25, px: { xs: 0.5, sm: 1 }, py: compact ? 0.6 : 0.85,
              cursor: 'pointer', flexShrink: 0, flex: 1,
              borderBottom: active ? `2.5px solid ${color}` : '2.5px solid transparent',
              color: active ? color : 'text.secondary',
              bgcolor: active ? `${color}0C` : 'transparent',
              transition: 'all 0.15s ease',
              '&:hover': { bgcolor: `${color}08`, color },
            }}
          >
            {React.cloneElement(tab.icon, { sx: { fontSize: compact ? 17 : 19, color: active ? color : 'inherit' } })}
            <Typography sx={{
              fontSize: '0.52rem', fontWeight: active ? 700 : 500,
              lineHeight: 1, letterSpacing: 0.2, whiteSpace: 'nowrap',
              color: active ? color : 'text.secondary',
            }}>
              {tab.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

// ── Constants shared by HubsPanel ────────────────────────────────────────────
const PANEL_HUB_ICONS = ['📚', '🎓', '🔬', '📖', '💡', '🎯', '🚀', '⚡', '🌟', '🧠', '✏️', '🔭', '🎨', '🏆', '📝'];
const PANEL_HUB_COLORS = ['#6366f1', '#f43f5e', '#f59e0b', '#10b981', '#0ea5e9', '#8b5cf6', '#f97316', '#14b8a6', '#ec4899', '#84cc16'];
const PANEL_EMPTY_FORM = { name: '', description: '', icon: '📚', color: '#6366f1', pdfIds: [] };

// Compact Learning Hubs panel rendered directly in the dashboard right panel
function HubsPanel({ user, onOpenHub, onManageHubs, onClose, driveConnected }) {
  const [hubs, setHubs] = useState([]);
  const [allPdfs, setAllPdfs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(PANEL_EMPTY_FORM);
  const [createSaving, setCreateSaving] = useState(false);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editingHub, setEditingHub] = useState(null);
  const [editForm, setEditForm] = useState(PANEL_EMPTY_FORM);
  const [editSaving, setEditSaving] = useState(false);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [hubToDelete, setHubToDelete] = useState(null);
  const [deleteOption, setDeleteOption] = useState('hub-only');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadHubs = () => {
    setLoading(true);
    Promise.all([
      getAllLearningHubs(user?.uid),
      libraryService.getAllLibraryItems().catch(() => []),
    ])
      .then(([hubData, pdfData]) => {
        setHubs(hubData || []);
        setAllPdfs(pdfData || []);
      })
      .catch(() => { setHubs([]); setAllPdfs([]); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadHubs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, driveConnected]);

  // ── Create handlers ──
  const openCreateHub = () => {
    setCreateForm(PANEL_EMPTY_FORM);
    setCreateOpen(true);
  };

  const handleCreateSave = async () => {
    if (!createForm.name.trim()) return;
    setCreateSaving(true);
    try {
      await createLearningHub({ ...createForm, userId: user?.uid });
      setCreateOpen(false);
      loadHubs();
    } catch (err) {
      console.error('❌ Failed to create hub:', err);
    } finally {
      setCreateSaving(false);
    }
  };

  // ── Edit handlers ──
  const openEdit = (e, hub) => {
    e.stopPropagation();
    setEditingHub(hub);
    setEditForm({
      name: hub.name,
      description: hub.description || '',
      icon: hub.icon || '📚',
      color: hub.color || '#6366f1',
      pdfIds: hub.pdfIds || [],
    });
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!editForm.name.trim() || !editingHub) return;
    setEditSaving(true);
    try {
      await updateLearningHub(editingHub.id, editForm);
      setEditOpen(false);
      setEditingHub(null);
      loadHubs();
    } catch (err) {
      console.error('❌ Failed to update hub:', err);
    } finally {
      setEditSaving(false);
    }
  };

  const toggleEditPdf = (pdfId) => {
    setEditForm(prev => ({
      ...prev,
      pdfIds: prev.pdfIds.includes(pdfId)
        ? prev.pdfIds.filter(id => id !== pdfId)
        : [...prev.pdfIds, pdfId],
    }));
  };

  // ── Delete handlers ──
  const openDelete = (e, hub) => {
    e.stopPropagation();
    setHubToDelete(hub);
    setDeleteOption('hub-only');
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!hubToDelete) return;
    setDeleteLoading(true);
    try {
      if (deleteOption === 'hub-and-pdfs') {
        const pdfsToDelete = allPdfs.filter(p => (hubToDelete.pdfIds || []).includes(p.id));
        await Promise.all(pdfsToDelete.map(p => libraryService.removePDFFromLibrary(p.id)));
      }
      await deleteLearningHub(hubToDelete.id);
      setDeleteOpen(false);
      setHubToDelete(null);
      loadHubs();
    } catch (err) {
      console.error('❌ Failed to delete hub:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const pdfCountForDelete = hubToDelete
    ? allPdfs.filter(p => (hubToDelete.pdfIds || []).includes(p.id)).length
    : 0;

  // ── Loading skeleton ──
  if (loading) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: { xs: 2, sm: 3, md: 4 }, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <Box>
            <Skeleton width={80} height={14} />
            <Skeleton width={160} height={32} />
          </Box>
          <Skeleton variant="rounded" width={100} height={34} />
        </Box>
        <Divider />
        <Box sx={{ flex: 1, px: { xs: 1.5, sm: 2, md: 3 }, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {[0, 1, 2].map(i => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Skeleton variant="rounded" width={44} height={44} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width="60%" height={18} />
                <Skeleton width="80%" height={14} sx={{ mt: 0.5 }} />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* ── Header ── */}
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: { xs: 2, sm: 3, md: 4 }, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onClose && (
            <IconButton size="small" onClick={onClose} sx={{ mr: 0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
          <Box>
            <Typography variant="overline" color="text.disabled" fontWeight={700} sx={{ letterSpacing: 2, fontSize: '0.65rem' }}>
              Your Hubs
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.5px' }}>
                Learning Hubs
              </Typography>
              {hubs.length > 0 && (
                <Chip label={`${hubs.length}`} size="small" sx={{ fontWeight: 700, fontSize: '0.7rem', height: 20 }} />
              )}
            </Box>
          </Box>
        </Box>
        <Button
          variant="outlined" size="small" startIcon={<AddIcon />}
          onClick={openCreateHub}
          sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
        >
          New Hub
        </Button>
      </Box>

      <Divider />

      {/* ── Hub list ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 1.5, sm: 2, md: 3 }, py: 2 }}>
        {hubs.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h2" sx={{ mb: 2 }}>📚</Typography>
            <Typography variant="h6" fontWeight={700} gutterBottom>No hubs yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first hub to start studying smarter.
            </Typography>
            <Button variant="contained" onClick={openCreateHub} sx={{ textTransform: 'none', borderRadius: 2 }}>
              Create a Hub
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {hubs.map((hub, i) => {
              const color = hub.color || PANEL_HUB_COLORS[i % PANEL_HUB_COLORS.length];
              const pdfCount = hub.pdfIds?.length || hub.pdfCount || 0;
              return (
                <Box
                  key={hub.id}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    p: 1.5, borderRadius: 2, cursor: 'pointer',
                    border: '1px solid', borderColor: 'divider',
                    borderLeft: `3px solid ${color}`,
                    transition: 'all 0.18s ease',
                    '&:hover': { borderColor: color, bgcolor: `${color}08`, transform: 'translateX(2px)' },
                  }}
                  onClick={() => onOpenHub(hub)}
                >
                  {/* Icon */}
                  <Box sx={{
                    width: 40, height: 40, borderRadius: 1.5, flexShrink: 0,
                    bgcolor: `${color}18`, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: 20,
                  }}>
                    {hub.icon || '📚'}
                  </Box>

                  {/* Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={700} noWrap sx={{ fontSize: '0.9rem', lineHeight: 1.3 }}>
                      {hub.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25, flexWrap: 'wrap' }}>
                      <Chip
                        label={`${pdfCount} PDF${pdfCount !== 1 ? 's' : ''}`}
                        size="small"
                        sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: `${color}14`, color, '& .MuiChip-label': { px: 0.75 } }}
                      />
                      {hub.description && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.72rem' }}>
                          {hub.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Actions — only for real (non-sample) hubs */}
                  {!hub.isSample && (
                    <Box sx={{ display: 'flex', flexShrink: 0, gap: 0.25 }}>
                      <Tooltip title="Edit hub" placement="top">
                        <IconButton size="small" onClick={(e) => openEdit(e, hub)}
                          sx={{ color: 'text.disabled', p: 0.5, '&:hover': { color: 'primary.main', bgcolor: 'primary.50' } }}>
                          <EditIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete hub" placement="top">
                        <IconButton size="small" onClick={(e) => openDelete(e, hub)}
                          sx={{ color: 'text.disabled', p: 0.5, '&:hover': { color: 'error.main', bgcolor: 'error.50' } }}>
                          <DeleteIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}

                  <ArrowIcon sx={{ color: 'text.disabled', fontSize: 16, flexShrink: 0 }} />
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* ── Create Dialog ── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth
        sx={{ '& .MuiDialog-paper': { m: { xs: 1, sm: 2 }, maxHeight: '90vh' } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.05rem', pb: 1 }}>Create New Hub</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <TextField
            autoFocus label="Hub Name" fullWidth size="small"
            value={createForm.name}
            onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateSave()}
            placeholder="e.g., Physics – Class 12"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Description (optional)" fullWidth size="small" multiline rows={2}
            value={createForm.description}
            onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
            sx={{ mb: 2.5 }}
          />

          {/* Icon picker */}
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Icon
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, mb: 2.5, flexWrap: 'wrap' }}>
            {PANEL_HUB_ICONS.map(icon => (
              <IconButton key={icon} onClick={() => setCreateForm({ ...createForm, icon })}
                sx={{
                  border: '1.5px solid', fontSize: '1.125rem', width: 38, height: 38, borderRadius: 1.5,
                  borderColor: createForm.icon === icon ? 'primary.main' : 'divider',
                  bgcolor: createForm.icon === icon ? 'primary.50' : 'transparent',
                }}>
                {icon}
              </IconButton>
            ))}
          </Box>

          {/* Color picker */}
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Color
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, mb: 2.5, flexWrap: 'wrap' }}>
            {PANEL_HUB_COLORS.map(color => (
              <Tooltip key={color} title={color} placement="top">
                <IconButton onClick={() => setCreateForm({ ...createForm, color })}
                  sx={{
                    bgcolor: color, width: 32, height: 32, borderRadius: '50%',
                    outline: createForm.color === color ? `3px solid ${color}` : 'none',
                    outlineOffset: 2, '&:hover': { bgcolor: color, opacity: 0.85 },
                  }}>
                  {createForm.color === color && <Box sx={{ color: '#fff', fontSize: '0.8rem', fontWeight: 700, lineHeight: 1 }}>✓</Box>}
                </IconButton>
              </Tooltip>
            ))}
          </Box>

          {/* PDF selection */}
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Add PDFs (optional)
            {createForm.pdfIds.length > 0 && (
              <Box component="span" sx={{ ml: 1, color: 'primary.main', textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}>
                {createForm.pdfIds.length} selected
              </Box>
            )}
          </Typography>
          {allPdfs.length > 0 ? (
            <Paper variant="outlined" sx={{ maxHeight: 180, overflow: 'auto', borderRadius: 1.5 }}>
              <List dense disablePadding>
                {allPdfs.map((pdf, idx) => (
                  <React.Fragment key={pdf.id}>
                    {idx > 0 && <Divider component="li" />}
                    <ListItem button onClick={() => setCreateForm(prev => ({
                      ...prev,
                      pdfIds: prev.pdfIds.includes(pdf.id)
                        ? prev.pdfIds.filter(id => id !== pdf.id)
                        : [...prev.pdfIds, pdf.id],
                    }))} sx={{ py: 0.75, px: 1.5 }}>
                      <IconButton size="small" sx={{ pointerEvents: 'none', mr: 0.5 }}>
                        {createForm.pdfIds.includes(pdf.id)
                          ? <CheckBoxIcon color="primary" sx={{ fontSize: 18 }} />
                          : <CheckBoxOutlineBlankIcon sx={{ fontSize: 18 }} />}
                      </IconButton>
                      <ListItemText
                        primary={pdf.name}
                        secondary={`${pdf.totalPages || pdf.pageCount || '?'} pages`}
                        primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          ) : (
            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
              No PDFs in library yet — you can add them later
            </Typography>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateSave} disabled={createSaving || !createForm.name.trim()}
            startIcon={createSaving ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}>
            {createSaving ? 'Creating…' : 'Create Hub'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Dialog ── */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth
        sx={{ '& .MuiDialog-paper': { m: { xs: 1, sm: 2 }, maxHeight: '90vh' } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.05rem', pb: 1 }}>Edit Hub</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <TextField
            autoFocus label="Hub Name" fullWidth size="small"
            value={editForm.name}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Description (optional)" fullWidth size="small" multiline rows={2}
            value={editForm.description}
            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
            sx={{ mb: 2.5 }}
          />

          {/* Icon picker */}
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Icon
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, mb: 2.5, flexWrap: 'wrap' }}>
            {PANEL_HUB_ICONS.map(icon => (
              <IconButton key={icon} onClick={() => setEditForm({ ...editForm, icon })}
                sx={{
                  border: '1.5px solid', fontSize: '1.125rem', width: 38, height: 38, borderRadius: 1.5,
                  borderColor: editForm.icon === icon ? 'primary.main' : 'divider',
                  bgcolor: editForm.icon === icon ? 'primary.50' : 'transparent',
                }}>
                {icon}
              </IconButton>
            ))}
          </Box>

          {/* Color picker */}
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Color
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, mb: 2.5, flexWrap: 'wrap' }}>
            {PANEL_HUB_COLORS.map(color => (
              <Tooltip key={color} title={color} placement="top">
                <IconButton onClick={() => setEditForm({ ...editForm, color })}
                  sx={{
                    bgcolor: color, width: 32, height: 32, borderRadius: '50%',
                    outline: editForm.color === color ? `3px solid ${color}` : 'none',
                    outlineOffset: 2, '&:hover': { bgcolor: color, opacity: 0.85 },
                  }}>
                  {editForm.color === color && <Box sx={{ color: '#fff', fontSize: '0.8rem', fontWeight: 700, lineHeight: 1 }}>✓</Box>}
                </IconButton>
              </Tooltip>
            ))}
          </Box>

          {/* PDF management */}
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            PDFs in this hub
            {editForm.pdfIds.length > 0 && (
              <Box component="span" sx={{ ml: 1, color: 'primary.main', textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}>
                {editForm.pdfIds.length} selected
              </Box>
            )}
          </Typography>
          {allPdfs.length > 0 ? (
            <Paper variant="outlined" sx={{ maxHeight: 180, overflow: 'auto', borderRadius: 1.5 }}>
              <List dense disablePadding>
                {allPdfs.map((pdf, idx) => (
                  <React.Fragment key={pdf.id}>
                    {idx > 0 && <Divider component="li" />}
                    <ListItem button onClick={() => toggleEditPdf(pdf.id)} sx={{ py: 0.75, px: 1.5 }}>
                      <IconButton size="small" sx={{ pointerEvents: 'none', mr: 0.5 }}>
                        {editForm.pdfIds.includes(pdf.id)
                          ? <CheckBoxIcon color="primary" sx={{ fontSize: 18 }} />
                          : <CheckBoxOutlineBlankIcon sx={{ fontSize: 18 }} />}
                      </IconButton>
                      <ListItemText
                        primary={pdf.name}
                        secondary={`${pdf.totalPages || pdf.pageCount || '?'} pages`}
                        primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          ) : (
            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
              No PDFs in library yet
            </Typography>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
          <Button onClick={() => setEditOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editSaving || !editForm.name.trim()}
            startIcon={editSaving ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}>
            {editSaving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Dialog ── */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1, fontWeight: 700, fontSize: '1.05rem' }}>
          <WarningIcon color="error" fontSize="small" />
          Delete "{hubToDelete?.name}"?
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This hub contains <strong>{pdfCountForDelete} PDF{pdfCountForDelete !== 1 ? 's' : ''}</strong>. What would you like to do?
          </Typography>
          <RadioGroup value={deleteOption} onChange={e => setDeleteOption(e.target.value)}>
            <FormControlLabel
              value="hub-only"
              control={<Radio size="small" />}
              sx={{ alignItems: 'flex-start', mb: 1, '& .MuiRadio-root': { pt: 0.25 } }}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>Remove hub only</Typography>
                  <Typography variant="caption" color="text.secondary">Keep all PDFs in your library</Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="hub-and-pdfs"
              control={<Radio size="small" color="error" />}
              sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.25 } }}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600} color="error.main">Remove hub and delete all PDFs</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Permanently deletes all {pdfCountForDelete} PDF{pdfCountForDelete !== 1 ? 's' : ''} from your library
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
            sx={{ textTransform: 'none', fontWeight: 600 }}>
            {deleteLoading ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function LandingDashboard({
  user,
  onOpenHubs,
  onOpenHub,
  hubCount,
  dashboardNav,
  onSignIn,
  onSignOut,
  onOpenSettings,
  subscription,
  driveConnected,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isLoggedIn = !!user;
  const { featureFlags } = useFeatureFlags(user);
  const [selectedNav, setSelectedNav] = useState(null);
  const [infoTab, setInfoTab] = useState('getstarted');
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const rightPanelRef = useRef(null);

  useEffect(() => {
    console.log('🏠 LandingDashboard: feature flags:', featureFlags);
  }, [featureFlags]);

  useEffect(() => { setSelectedNav(null); }, [isLoggedIn]);

  // dashboardNav is a signal object { nav, _t } — _t changes on every click so this
  // always fires, even if nav is the same value (e.g. clicking Learning Hubs twice).
  useEffect(() => {
    if (dashboardNav != null) setSelectedNav(dashboardNav.nav ?? null);
  }, [dashboardNav]);

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const getUserFirstName = () => {
    if (!user) return '';
    if (user.displayName) return user.displayName.split(' ')[0].toLowerCase();
    if (user.email) return user.email.split('@')[0].toLowerCase();
    return '';
  };

  const selectNav = useCallback((id) => {
    setSelectedNav(prev => prev === id ? null : id);
  }, []);

  const gradientText = {
    background: 'linear-gradient(135deg, #1b496b 0%, #1e6fa8 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  };

  const gradientBtn = {
    textTransform: 'none', borderRadius: 2, px: 4, py: 1.4, fontWeight: 700, fontSize: '1rem',
    boxShadow: '0 4px 14px rgba(27,73,107,0.35)',
    background: 'linear-gradient(135deg, #1b496b 0%, #1e6fa8 100%)',
    '&:hover': { background: 'linear-gradient(135deg, #1b496b 0%, #1e6fa8 100%)', boxShadow: '0 6px 20px rgba(27,73,107,0.5)' },
  };

  // Shared props that embed MUI Dialogs in the right panel.
  // KEY TRICK: the right panel has `transform: translateZ(0)` which makes it the
  // containing block for `position: fixed` children — scoping the dialog to the panel.
  const EMBEDDED_DIALOG_PROPS = {
    disablePortal: true,
    hideBackdrop: true,
    disableScrollLock: true,
    disableEnforceFocus: true,
    fullScreen: true,
    PaperProps: {
      elevation: 0,
      sx: {
        m: 0, borderRadius: 0,
        width: '100%', height: '100%',
        maxWidth: 'none', maxHeight: 'none',
        boxShadow: 'none', overflow: 'hidden',
      },
    },
  };

  const NAV_ITEMS = [
    { id: 'hubs', icon: <LibraryIcon />, label: 'Learning Hubs', color: '#ec4899', badge: hubCount > 0 ? `${hubCount} hub${hubCount !== 1 ? 's' : ''}` : null },
  ];

  const LAB_ITEMS = featureFlags?.labs
    ? [
        { id: 'math', icon: <MathIcon />, label: 'Math Lab', color: '#3b82f6' },
        { id: 'chemistry', icon: <ChemistryIcon />, label: 'Chemistry', color: '#ef4444' },
        { id: 'physics', icon: <PhysicsIcon />, label: 'Physics', color: '#a855f7' },

        { id: 'globe', icon: <GlobeIcon />, label: 'Globe Viewer', color: '#14b8a6' },
        { id: 'circuit', icon: <CircuitIcon />, label: 'Circuit Lab', color: '#00b894' },
      ]
    : [];

  const NavItem = ({ item, size = 'md' }) => {
    const active = selectedNav === item.id;
    return (
      <Box
        onClick={() => selectNav(item.id)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 1.5, py: size === 'sm' ? 0.85 : 1.1,
          borderRadius: 2, cursor: 'pointer', mb: 0.25,
          bgcolor: active ? `${item.color}18` : 'transparent',
          border: '1px solid', borderColor: active ? `${item.color}45` : 'transparent',
          '&:hover': { bgcolor: `${item.color}10`, borderColor: `${item.color}25` },
          transition: 'all 0.15s ease', userSelect: 'none',
        }}
      >
        <Box sx={{ color: active ? item.color : 'text.secondary', display: 'flex', flexShrink: 0 }}>
          {React.cloneElement(item.icon, { sx: { fontSize: size === 'sm' ? 17 : 19 } })}
        </Box>
        <Typography
          variant="body2"
          sx={{ flex: 1, fontWeight: active ? 700 : 500, color: active ? item.color : 'text.primary', fontSize: size === 'sm' ? '0.8rem' : '0.875rem' }}
        >
          {item.label}
        </Typography>
        {item.badge && (
          <Chip
            label={item.badge} size="small"
            sx={{ height: 17, fontSize: '0.6rem', fontWeight: 700, border: 'none', bgcolor: item.urgent ? 'error.main' : `${item.color}18`, color: item.urgent ? 'white' : item.color }}
          />
        )}
      </Box>
    );
  };

  const PricingSection = () => {
    const [checkoutLoading, setCheckoutLoading] = React.useState(null); // 'monthly' | 'yearly' | null

    const handlePlanClick = async (planId) => {
      if (planId === 'free') { onSignIn(); return; }
      // Paid plan — require login first, then open Razorpay
      if (!isLoggedIn || !user) { onSignIn(); return; }
      const isYearly = planId === 'yearly';
      setCheckoutLoading(planId);
      try {
        const userName = user.displayName || user.email?.split('@')[0] || 'User';
        await createRazorpayCheckout('STUDENT', isYearly, user.uid, user.email, userName);
      } catch (e) {
        console.error('Checkout error:', e);
      } finally {
        setCheckoutLoading(null);
      }
    };

    const plans = [
      {
        id: 'free', label: 'Free', sublabel: 'forever',
        price: '₹0', period: '/forever', note: null,
        badge: null,
        features: FREE_FEATURES,
        btnLabel: 'Start Free', btnStyle: 'outlined',
        accent: null, dark: false,
      },
      {
        id: 'monthly', label: 'Student', sublabel: 'monthly',
        price: '₹299', period: '/month', note: null,
        badge: { text: 'POPULAR', color: '#fbbf24', textColor: '#1a1a1a' },
        features: STUDENT_FEATURES,
        btnLabel: 'Get Monthly', btnStyle: 'dark',
        accent: null, dark: true,
      },
      {
        id: 'yearly', label: 'Student', sublabel: 'yearly',
        price: '₹2,499', period: '/year', note: '₹208/mo · Save 30%',
        badge: { text: 'BEST VALUE', color: '#10b981', textColor: 'white' },
        features: STUDENT_FEATURES,
        btnLabel: 'Get Yearly', btnStyle: 'accent',
        accent: '#10b981', dark: false,
      },
    ];

    return (
      <Box sx={{ px: { xs: 2, md: 3 }, pt: 2, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <Typography variant="overline" fontWeight={700} sx={{ letterSpacing: 3, color: 'text.disabled' }}>
            Simple Pricing
          </Typography>
          <Box sx={{ px: 1.25, py: 0.3, borderRadius: 10, bgcolor: '#10b98118', border: '1px solid #10b98135' }}>
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#10b981' }}>Yearly saves 30%</Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
          {plans.map(plan => (
            <Box key={plan.id} sx={{
              p: 2.5, borderRadius: 2.5, border: '1px solid',
              borderColor: plan.dark ? 'transparent' : plan.accent ? '#10b98145' : 'divider',
              bgcolor: plan.accent ? '#10b98108' : 'background.default',
              background: plan.dark ? 'linear-gradient(135deg, #1b496b 0%, #1e6fa8 100%)' : undefined,
              display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden',
            }}>
              {plan.badge && (
                <Box sx={{ position: 'absolute', top: 9, right: 9, px: 1, py: 0.3, borderRadius: 10, bgcolor: plan.badge.color }}>
                  <Typography sx={{ fontSize: '0.52rem', fontWeight: 800, color: plan.badge.textColor, letterSpacing: 0.5 }}>
                    {plan.badge.text}
                  </Typography>
                </Box>
              )}
              <Box sx={{ mb: 0.5 }}>
                <Typography variant="subtitle2" fontWeight={800} sx={{ color: plan.dark ? 'white' : 'text.primary', lineHeight: 1.2 }}>
                  {plan.label}
                </Typography>
                <Typography variant="caption" sx={{ color: plan.dark ? 'rgba(255,255,255,0.55)' : 'text.disabled' }}>
                  {plan.sublabel}
                </Typography>
              </Box>
              <Box sx={{ mb: 1.5 }}>
                <Typography component="span" variant="h5" fontWeight={800} sx={{ color: plan.dark ? 'white' : plan.accent || 'text.primary', lineHeight: 1 }}>
                  {plan.price}
                </Typography>
                <Typography component="span" sx={{ fontSize: '0.75rem', color: plan.dark ? 'rgba(255,255,255,0.65)' : 'text.secondary' }}>
                  {plan.period}
                </Typography>
                {plan.note && (
                  <Typography sx={{ fontSize: '0.68rem', color: plan.dark ? 'rgba(255,255,255,0.5)' : '#10b981', display: 'block', mt: 0.25, fontWeight: 600 }}>
                    {plan.note}
                  </Typography>
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                {plan.features.map((f, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.75 }}>
                    <CheckCircleIcon sx={{ fontSize: 13, color: plan.dark ? '#fbbf24' : plan.accent || '#10b981', mt: '2px', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.75rem', color: plan.dark ? 'rgba(255,255,255,0.85)' : 'text.secondary', lineHeight: 1.5 }}>{f}</Typography>
                  </Box>
                ))}
              </Box>
              <Button
                variant={plan.btnStyle === 'outlined' ? 'outlined' : 'contained'}
                fullWidth
                disabled={checkoutLoading === plan.id}
                onClick={() => handlePlanClick(plan.id)}
                sx={{
                  mt: 2, borderRadius: 1.5, fontWeight: 700, fontSize: '0.8rem', py: 0.75,
                  ...(plan.btnStyle === 'dark'
                    ? { bgcolor: 'white', color: '#1b496b', '&:hover': { bgcolor: '#f0f6fb' }, boxShadow: 'none' }
                    : plan.btnStyle === 'accent'
                    ? { bgcolor: plan.accent, color: 'white', borderColor: plan.accent, '&:hover': { bgcolor: '#059669' }, boxShadow: 'none' }
                    : { borderColor: '#1b496b', color: '#1b496b', '&:hover': { bgcolor: '#1b496b08' } }
                  ),
                }}
              >
                {checkoutLoading === plan.id ? 'Loading…' : plan.btnLabel}
              </Button>
            </Box>
          ))}
        </Box>

        {/* Login-required notice — shown only when user is not signed in */}
        {!isLoggedIn && (
          <Box sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1,
            px: 1.5, py: 1, borderRadius: 1.5,
            bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider',
          }}>
            <LoginIcon sx={{ fontSize: 15, color: 'text.disabled', mt: '2px', flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55 }}>
              <strong>Sign in required to purchase</strong> — clicking a paid plan will ask you to sign in with Google first. Your plan selection will continue automatically after login.
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Map nav id → display label for the panel header bar
  const TOOL_LABELS = {
    math: 'Math Lab',
    chemistry: 'Chemistry',
    physics: 'Physics',
    code: 'Code Editor',
    globe: 'Globe Viewer',
    circuit: 'Circuit Lab',
  };

  const close = () => setSelectedNav(null);

  return (
    <Box sx={{ height: '100%', overflow: 'hidden', display: 'flex', bgcolor: 'background.default' }}>

      {/* ── LEFT SIDEBAR ── */}
      <Box
        sx={{
          width: { xs: '100%', md: isLoggedIn ? 'auto' : '34%' },
          minWidth: { md: isLoggedIn ? 220 : 'unset' },
          maxWidth: { md: isLoggedIn ? 290 : 'unset' },
          flexShrink: 0,
          display: isMobile && isLoggedIn && selectedNav ? 'none' : 'flex',
          flexDirection: 'column',
          px: { xs: isLoggedIn ? 2.5 : 3, md: isLoggedIn ? 2.5 : 4 },
          py: { xs: 3, md: 5 },
          overflowY: 'auto',
          overflowX: 'hidden',
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(145deg, rgba(27,73,107,0.18) 0%, rgba(30,111,168,0.14) 100%)'
            : 'linear-gradient(145deg, rgba(27,73,107,0.07) 0%, rgba(30,111,168,0.05) 100%)',
          borderRight: { md: '1px solid' }, borderColor: { md: 'divider' },
        }}
      >
        {isLoggedIn ? (
          <>
            {/* Greeting */}
            <Box sx={{ mb: 3, px: 0.5 }}>
              <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 2, fontSize: '0.68rem' }}>
                {getGreeting()}
              </Typography>
              <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5, lineHeight: 1.15, letterSpacing: '-0.5px', whiteSpace: 'nowrap' }}>
                Welcome back,<br />
                <Box component="span" sx={gradientText}>{getUserFirstName()}.</Box>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', lineHeight: 1.6 }}>
                Your learning workspace is ready.
              </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Free trial notice — only for free-tier users using the shared eval key */}
            {hasDefaultKey(PROVIDERS.OPENROUTER) && subscription?.isFree && !subscription?.usage?.unlimited && (
              <Alert
                severity="warning"
                icon={<KeyIcon fontSize="small" />}
                sx={{ mb: 2, borderRadius: 2, py: 0.75, fontSize: '0.75rem', alignItems: 'flex-start' }}
                action={
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    onClick={() => onOpenSettings?.('ai')}
                    sx={{ whiteSpace: 'nowrap', borderRadius: 1.5, fontSize: '0.7rem', mt: 0.25 }}
                  >
                    Configure
                  </Button>
                }
              >
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.25 }}>
                  Free Trial — Limited Tokens
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  We provide a small shared pool for evaluation. For unlimited AI, generate your own free Gemini or Groq key.
                </Typography>
              </Alert>
            )}

            <Typography variant="overline" color="text.disabled" fontWeight={700} sx={{ mb: 1, letterSpacing: 2, fontSize: '0.62rem', px: 0.5 }}>
              Quick Launch
            </Typography>
            {NAV_ITEMS.map(item => <NavItem key={item.id} item={item} />)}

            {LAB_ITEMS.length > 0 && (
              <>
                <Divider sx={{ mt: 2, mb: 2 }} />
                <Typography variant="overline" color="text.disabled" fontWeight={700} sx={{ mb: 1, letterSpacing: 2, fontSize: '0.62rem', px: 0.5 }}>
                  Labs
                </Typography>
                {LAB_ITEMS.map(item => <NavItem key={item.id} item={item} size="sm" />)}
              </>
            )}

            <Divider sx={{ mt: 2, mb: 2 }} />
            <Typography variant="overline" color="text.disabled" fontWeight={700} sx={{ mb: 1, letterSpacing: 2, fontSize: '0.62rem', px: 0.5 }}>
              Feedback
            </Typography>
            <Box
              onClick={() => setFeedbackOpen(true)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.1, borderRadius: 2, cursor: 'pointer', mb: 0.25,
                border: '1px solid', borderColor: 'transparent',
                '&:hover': { bgcolor: '#f59e0b10', borderColor: '#f59e0b25' },
                transition: 'all 0.15s ease', userSelect: 'none',
              }}
            >
              <FeedbackIcon sx={{ fontSize: 19, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ flex: 1, fontWeight: 500, color: 'text.primary', fontSize: '0.875rem' }}>
                Send Feedback
              </Typography>
            </Box>

            <Box sx={{ mt: 'auto', pt: 3, px: 0.5 }}>
              {onSignOut && (
                <Button
                  size="small"
                  startIcon={<LoginIcon sx={{ transform: 'rotate(180deg)' }} />}
                  onClick={onSignOut}
                  sx={{ textTransform: 'none', color: 'text.secondary', mb: 1.5, fontWeight: 500, fontSize: '0.8rem', pl: 0 }}
                >
                  Sign out
                </Button>
              )}
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                © {new Date().getFullYear()} Ekamanam · ఏకమనం
              </Typography>
            </Box>
          </>
        ) : (
          /* Logged-out hero */
          <>
            <Typography variant="h2" fontWeight={800} sx={{ mb: 2, lineHeight: 1.05, letterSpacing: '-2px', fontSize: { xs: '2rem', md: '2.6rem' }, textAlign: { xs: 'center', md: 'left' } }}>
              A Thinking Partner.<br />
              <Box component="span" sx={gradientText}>Not a Shortcut.</Box>
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.8, textAlign: { xs: 'center', md: 'left' } }}>
              Upload any textbook or study material, then learn with AI — in your own language. Ekamanam doesn't hand you answers. It prompts reflection, breaks down complexity, and builds the understanding that lasts beyond the exam.
            </Typography>

            <Button variant="contained" size="large" onClick={onSignIn} startIcon={<LoginIcon />} sx={{ ...gradientBtn, alignSelf: { xs: 'center', md: 'flex-start' }, mb: 2 }}>
              Sign in with Google
            </Button>

            {/* Lottie animation — full sidebar width */}
            <Box
              component="iframe"
              src="https://lottie.host/embed/5c264b17-229b-4398-81b5-e425c42686a0/hmywwKD5IT.lottie"
              title="Ekamanam animation"
              sx={{
                width: '100%',
                height: 'auto',
                aspectRatio: '1 / 1',
                border: 'none',
                display: 'block',
                background: 'transparent',
                flex: 1,
                minHeight: 0,
              }}
            />

            {isMobile && (
              <Box sx={{ mt: 2, mx: -3 }}>
                <InfoTopNav infoTab={infoTab} setInfoTab={setInfoTab} compact />
                <Box sx={{ px: 3 }}>
                  {infoTab === 'getstarted'  && <GetStartedSection onSignIn={onSignIn} user={user} driveConnected={driveConnected} onOpenSettings={onOpenSettings} hubCount={hubCount} onSelectNav={selectNav} />}
                  {infoTab === 'features'    && <HubFeaturesGrid />}
                  {infoTab === 'studyplan'   && <StudyPlanInfoSection />}
                  {infoTab === 'labs'        && <LabsSection />}
                  {infoTab === 'personalize' && <PersonalizeSection />}
                  {infoTab === 'journey'     && <LearningJourneySection />}
                  {infoTab === 'pricing'     && <PricingSection />}
                </Box>
              </Box>
            )}

            <Typography variant="caption" color="text.disabled" sx={{ mt: 'auto', pt: 3, display: 'block' }}>
              © {new Date().getFullYear()} Ekamanam · ఏకమనం
            </Typography>
          </>
        )}
      </Box>

      {/* ── RIGHT PANEL ──
          style.transform (not sx) is used to guarantee the CSS property is applied.
          Any ancestor with transform !== none becomes the containing block for
          position:fixed children — so dialogs rendered with disablePortal are
          visually scoped to this panel, not the full viewport.
      */}
      <Box
        ref={rightPanelRef}
        style={{ transform: 'translateZ(0)' }}
        sx={{
          flex: 1,
          display: isMobile ? (isLoggedIn && selectedNav ? 'flex' : 'none') : 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          position: 'relative',
        }}
      >
        {/* Info panels — shown when no tool/hub is active */}
        {(!isLoggedIn || !selectedNav) && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Tab nav at the TOP of the right panel */}
            <InfoTopNav infoTab={infoTab} setInfoTab={setInfoTab} />
            <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {infoTab === 'getstarted'  && <GetStartedSection onSignIn={onSignIn} user={user} driveConnected={driveConnected} onOpenSettings={onOpenSettings} hubCount={hubCount} onSelectNav={selectNav} />}
              {infoTab === 'features'    && <HubFeaturesGrid />}
              {infoTab === 'studyplan'   && <StudyPlanInfoSection />}
              {infoTab === 'labs'        && <LabsSection />}
              {infoTab === 'personalize' && <PersonalizeSection />}
              {infoTab === 'journey'     && <LearningJourneySection />}
              {infoTab === 'pricing'     && <PricingSection />}
            </Box>
          </Box>
        )}

        {/* Learning Hubs — inline component, not a Dialog */}
        {isLoggedIn && selectedNav === 'hubs' && (
          <HubsPanel user={user} onOpenHub={onOpenHub} onManageHubs={onOpenHubs} onClose={close} driveConnected={driveConnected} />
        )}

        {/* Tool panels — only the active tool is mounted (conditional rendering).
            Each renders with disablePortal so its DOM stays inside this container,
            and the transform above scopes its position:fixed to this panel. */}
        {isLoggedIn && TOOL_LABELS[selectedNav] && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', zIndex: 2 }}>
            {/* Panel header — always visible above the embedded tool */}
            <Box sx={{
              display: 'flex', alignItems: 'center', px: 2, py: 1,
              borderBottom: '1px solid', borderColor: 'divider',
              bgcolor: 'background.paper', flexShrink: 0, zIndex: 3,
            }}>
              <IconButton size="small" onClick={close} sx={{ mr: 1 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
              <Typography variant="subtitle2" fontWeight={700}>
                {TOOL_LABELS[selectedNav]}
              </Typography>
            </Box>

            {/* Tool — fills the remaining height below the header */}
            <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }} style={{ transform: 'translateZ(0)' }}>
              {selectedNav === 'math' && (
                <MathLabV2 open onClose={close} user={user} dialogProps={EMBEDDED_DIALOG_PROPS} />
              )}
              {selectedNav === 'chemistry' && (
                <ChemistryTools open onClose={close} user={user} dialogProps={EMBEDDED_DIALOG_PROPS} />
              )}
              {selectedNav === 'physics' && (
                <PhysicsSimulator open onClose={close} user={user} dialogProps={EMBEDDED_DIALOG_PROPS} />
              )}
              {selectedNav === 'code' && (
                <CodeEditor open onClose={close} user={user} dialogProps={EMBEDDED_DIALOG_PROPS} />
              )}
              {selectedNav === 'globe' && (
                <GlobeViewer open onClose={close} user={user} dialogProps={EMBEDDED_DIALOG_PROPS} />
              )}
              {selectedNav === 'circuit' && (
                <CircuitSimulator open onClose={close} title="Circuit Lab" dialogProps={EMBEDDED_DIALOG_PROPS} />
              )}
            </Box>
          </Box>
        )}
      </Box>

      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} user={user} />
    </Box>
  );
}

export default LandingDashboard;
